import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Type } from '@angular/core';
import {
    AppointmentChangedEvent,
    AppointmentsReloadEvent,
    AppointmentTimeSlot,
    AppointmentTimeSlotSlotGroup,
    AppointmentViewModel,
    CONFIG_TOKEN,
    CONFIRM_DIALOG_TOKEN,
    ConfirmDialogLike,
    DialogableError,
    IConfig,
    RescheduleData,
    TimelineReloadEvent,
    UnfullfillableError,
} from '@pxl/models/common';
import {
    AppointmentFilter,
    AppointmentsBookingtimeslotsOptions,
    AppointmentService,
    CalendarService,
    DialogService,
    ExtendedAppointmentChangeResponseModel,
    LoadingService,
    LoginService,
    MakeOrRescheduleAppointmentParams,
    PxlApplicationStateService,
} from '@pxl/models/contracts';
import {
    AppointmentService as AppointmentBackendService,
    AppointmentChangeRequestModel,
    AppointmentLinkageModel,
    AppointmentModel,
    AppointmentReferencesModel,
    AppointmentReservationRequestModel,
    AppointmentReservationResponseModel,
    AppointmentToCreateModel,
    AppointmentToRescheduleModel,
    AppointmentToReserveModel,
    CategoryInformationResponse,
} from '@pxl/shared/generated';
import { RESOURCES } from '@pxl/shared/translation';
import { carriesValue } from '@pxl/shared/util';
import { CONFLICT } from 'http-status-codes';
import moment from 'moment-timezone';
import { Observable, throwError, zip } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

export type DayEnum = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const DayEnum: { [key: string]: DayEnum } = {
    NUMBER_0: 0,
    NUMBER_1: 1,
    NUMBER_2: 2,
    NUMBER_3: 3,
    NUMBER_4: 4,
    NUMBER_5: 5,
    NUMBER_6: 6,
};

export const appointmentOverviewResourceKeys = {
    headline: 'petsxl.ui.dashboard.appointment.appointment-overview.headline',
    appointmentCanceled: 'petsxl.ui.dashboard.appointment.appointment-overview.list.messages.appointment-canceled',
    rescheduledFrom: 'petsxl.ui.dashboard.appointment.appointment-overview.list.messages.rescheduled-from',
    buttonsFilter: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.filter',
    makeAppointment: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.make-appointment',
    buttonsChoose: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.choose',
    buttonsCancelAppointment: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.cancel-appointment',
    buttonsRescheduleAppointment: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.reschedule-appointment',
    buttonsShowDetails: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.show-appointment-details',
    buttonsMore: 'petsxl.ui.dashboard.appointment.appointment-overview.buttons.more',
    cancelAppointmentAlertTitle: 'petsxl.ui.dashboard.appointment.appointment-overview.cancel-appointment.alert.title',
    cancelAppointmentAlertMessage: 'petsxl.ui.dashboard.appointment.appointment-overview.cancel-appointment.alert.message',
    cancelAppointmentAlertButton: 'petsxl.ui.dashboard.appointment.appointment-overview.cancel-appointment.alert.execution.button',
    menuHeadline: 'petsxl.ui.dashboard.appointment.appointment-overview.menu.headline',
    noFilterResultsInformationText: 'petsxl.ui.dashboard.appointment.appointment-overview.no-filter-results.information-text',
    noFilterResultsHeadline: 'petsxl.ui.dashboard.appointment.appointment-overview.no-filter-results.information-headline',
    noFilterResultsSubText: 'petsxl.ui.dashboard.appointment.appointment-overview.no-filter-results.sub-text',
};

type CancellationError = 'ForbiddenByCategory' | 'ForbiddenByTimeWindow';

@Injectable()
export class DefaultAppointmentService implements AppointmentService {
    private readonly loginService = inject(LoginService);
    private readonly appointmentBackendService = inject(AppointmentBackendService);
    private readonly pxlApplicationStateService = inject(PxlApplicationStateService);
    private readonly calenderService = inject(CalendarService);
    private readonly dialogService = inject(DialogService);
    private readonly loadingService = inject(LoadingService);
    private readonly config = inject<IConfig>(CONFIG_TOKEN);
    private readonly confirmDialog = inject<Type<ConfirmDialogLike>>(CONFIRM_DIALOG_TOKEN);

    public reserveAppointment(
        veterinarianId: string,
        appointmentReservationOptionsId: string,
        appointmentTimeSlot: AppointmentTimeSlot,
        appointmentReason?: string | null,
        specificEmployeeRequested?: boolean | null,
    ): Observable<AppointmentReservationResponseModel> {
        return this.trySlotGroup(appointmentTimeSlot.slotGroups, (slotGroup) => {
            const appointmentsToReserve: AppointmentToReserveModel[] = slotGroup.slots.map((s) => ({
                slotId: s.slotId,
                reason: appointmentReason,
                specificEmployeeRequested,
            }));
            const model: AppointmentReservationRequestModel = {
                appointmentReservationOptionsId,
                appointmentsToReserve,
                veterinarianId,
            };
            return this.appointmentBackendService.reserve(model, this.config.API_VERSION);
        });
    }

    public getAppointmentBookingInformation(veterinarianId: string) {
        return this.appointmentBackendService.getAppointmentBookingInformation(
            veterinarianId,
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    public getAppointmentBookingInformationAsGuest(veterinarianId: string, culture: string) {
        return this.appointmentBackendService.getAppointmentBookingInformationAsGuest(veterinarianId, this.config.API_VERSION, culture);
    }

    public getAppointmentBookingTimeslots(options: AppointmentsBookingtimeslotsOptions) {
        const { veterinarianId, startUtc, endUtc, category, employeeName, appointmentsToIgnore } = options;
        return this.appointmentBackendService.getAppointmentBookingTimeSlots(
            {
                veterinarianId,
                startUtc: startUtc.toISOString(),
                endUtc: endUtc.toISOString(),
                category,
                employeeName,
                appointmentsToIgnore,
            },
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    public getAppointmentBookingTimeslotsAsGuest(options: AppointmentsBookingtimeslotsOptions) {
        const { veterinarianId, startUtc, endUtc, category, existingReservationIdentifierForGuest } = options;
        return this.appointmentBackendService.getAppointmentBookingTimeSlotsAsGuest(
            {
                veterinarianId,
                startUtc: startUtc.toISOString(),
                endUtc: endUtc.toISOString(),
                category,
                existingReservationIdentifierForGuest,
            },
            this.config.API_VERSION,
        );
    }

    public getAppointments(filter?: AppointmentFilter): Observable<AppointmentModel[]> {
        filter = filter ?? {};

        return this.appointmentBackendService.getAppointmentsFlat(
            this.config.API_VERSION,
            this.loginService.getToken(),
            filter.filterAppointmentIds,
            filter.filterVeterinarianIds,
            filter.filterPatientIds,
            filter.minDate,
            filter.maxDate,
            filter.isCancelled,
        );
    }

    public cancelAppointmentWithConfirmDialog(appointmentViewModel: AppointmentViewModel): void {
        if (appointmentViewModel.timeForReschedulingExpired) {
            this.handleAppointmentCannotBeChanged(appointmentViewModel, 'ForbiddenByTimeWindow');
            return;
        }

        this.dialogService
            .open(this.confirmDialog, {
                title: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.alert.title,
                confirmText: RESOURCES.petsxl.ui.shared.pxl.pxl_confirmation_dialog_modal.buttons.confirm,
                cancelText: RESOURCES.petsxl.ui.shared.pxl.pxl_confirmation_dialog_modal.buttons.cancel,
                bodyTitle: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.alert.message,
            })
            .events.confirmPressed.pipe(
                switchMap(() => this.gateOnCategoryChangeable(appointmentViewModel)),
                switchMap(() => this.loadingService.showWaitObservable(this.cancelAppointments([appointmentViewModel]))),
                catchError(({ status }: HttpErrorResponse) => {
                    if (status === 409) {
                        // if conflict.
                        this.temporarilyDisableAppointmentInteractions(appointmentViewModel);
                    }
                    throw new DialogableError({
                        dialogTitle: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.dialog_title,
                        title: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.title,
                        message: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.body,
                    });
                }),
            )
            .subscribe();
    }

    public rescheduleAppointment(appointmentViewModel: AppointmentViewModel, reschedule: (a: AppointmentViewModel) => any): void {
        if (appointmentViewModel.timeForReschedulingExpired) {
            this.handleAppointmentCannotBeChanged(appointmentViewModel, 'ForbiddenByTimeWindow');
            return;
        }

        this.gateOnCategoryChangeable(appointmentViewModel)
            .pipe(
                tap(() => {
                    reschedule(appointmentViewModel);
                }),
            )
            .subscribe();
    }

    public cancelAppointmentReservation(savedReservationId: string): Observable<void> {
        return this.appointmentBackendService.cancelReservations(savedReservationId, this.config.API_VERSION);
    }

    public getAppointmentReferences(appointmentId: string): Observable<AppointmentReferencesModel> {
        return this.appointmentBackendService.getAppointmentReferences(
            appointmentId,
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    public exportICal(appointmentVM: AppointmentViewModel): Promise<void> {
        const { veterinarian, patient, appointment } = appointmentVM;
        const startDate = moment(appointmentVM.appointment.startUtc);
        const endDate = moment(appointmentVM.appointment.endUtc);

        return this.calenderService.exportICal(`${patient.patientName}_${appointment.category}`, {
            summary: `${appointmentVM.patient.patientName} - ${appointmentVM.appointment.category}`,
            start: startDate.toDate(),
            end: endDate.toDate(),
            description: appointmentVM.appointment.reason,
            location: `${veterinarian.clinicName}, ${veterinarian.address.streetHouseNumber}, ${veterinarian.address.postalCode} ${veterinarian.address.city}`,
        });
    }

    public makeOrRescheduleAppointment({
        slot,
        ...params
    }: MakeOrRescheduleAppointmentParams): Observable<ExtendedAppointmentChangeResponseModel> {
        return this.trySlotGroup(slot.slotGroups, (slotGroup) =>
            this.doMakeOrRescheduleAppointment({
                ...params,
                slotGroup,
            }),
        );
    }

    private getAppointmentCategoryInformation(appointmentId: string): Observable<CategoryInformationResponse> {
        return this.appointmentBackendService.getAppointmentCategoryInformation(
            appointmentId,
            this.config.API_VERSION,
            this.loginService.getToken(),
        );
    }

    private handleAppointmentCannotBeChanged(appointmentViewModel: AppointmentViewModel, reason: CancellationError) {
        this.temporarilyDisableAppointmentInteractions(appointmentViewModel);

        const confirmText = appointmentViewModel.veterinarian?.phoneNumber
            ? RESOURCES.petsxl.ui.appointments.cancel.dialog.buttons.call_vet
            : RESOURCES.petsxl.ui.appointments.cancel.dialog.buttons.close;

        const cancelText = appointmentViewModel.veterinarian?.phoneNumber
            ? RESOURCES.petsxl.ui.appointments.cancel.dialog.buttons.close
            : undefined;

        const message =
            reason === 'ForbiddenByCategory'
                ? RESOURCES.petsxl.ui.appointments.cancel.dialog.message_category_does_not_allow_changes
                : RESOURCES.petsxl.ui.appointments.cancel.dialog.message_reschedule_window_expired;

        const ref = this.dialogService.open(this.confirmDialog, {
            title: RESOURCES.petsxl.ui.appointments.cancel.dialog.title,
            bodyTitle: RESOURCES.petsxl.ui.appointments.cancel.dialog.body_title,
            bodyText: message,
            confirmText,
            cancelText,
        });

        ref.events.confirmPressed
            .pipe(
                tap(() => window.open(`tel:${appointmentViewModel.veterinarian.phoneNumber}`, '_self')),
                take(1),
            )
            .subscribe();
    }

    private gateOnCategoryChangeable(appointmentViewModel: AppointmentViewModel): Observable<AppointmentViewModel> {
        return this.loadingService.showWaitObservable(this.getAppointmentCategoryInformation(appointmentViewModel.appointment.id)).pipe(
            map((response) => {
                if (response.category?.cannotBeChangedByUser === true) {
                    this.handleAppointmentCannotBeChanged(appointmentViewModel, 'ForbiddenByCategory');
                    return null;
                }
                return appointmentViewModel;
            }),
            catchError(() => {
                this.temporarilyDisableAppointmentInteractions(appointmentViewModel);
                throw new DialogableError({
                    dialogTitle: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.dialog_title,
                    title: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.title,
                    message: RESOURCES.petsxl.ui.dashboard.appointment.appointment_overview.cancel_appointment.error.body,
                });
            }),
            filter(carriesValue),
        );
    }

    private temporarilyDisableAppointmentInteractions(appointmentViewModel: AppointmentViewModel): void {
        appointmentViewModel.disableRescheduling();
        this.pxlApplicationStateService.raiseEventSpecific(new TimelineReloadEvent());
        this.pxlApplicationStateService.raiseEventSpecific(new AppointmentsReloadEvent());
    }

    private trySlotGroup<T>(
        slotGroups: AppointmentTimeSlotSlotGroup[],
        api: (slotGroup: AppointmentTimeSlotSlotGroup) => Observable<T>,
    ): Observable<T> {
        const [slotGroup, ...remainingSlotGroups] = slotGroups;
        if (!slotGroup) return throwError(() => new UnfullfillableError());
        return api(slotGroup).pipe(
            catchError((error: unknown) => {
                if (!(error instanceof HttpErrorResponse) || error.status !== CONFLICT) {
                    // only handle CONFLICT Errors
                    throw error;
                }
                return this.trySlotGroup(remainingSlotGroups, api);
            }),
        );
    }

    private doMakeOrRescheduleAppointment({
        slotGroup,
        reason,
        appointmentLinkageModels,
        veterinarianId,
        patientIds,
        rescheduleData,
        specificEmployeeRequested,
        patientInsurancesMap,
    }: {
        slotGroup: AppointmentTimeSlotSlotGroup;
        reason: string;
        appointmentLinkageModels: AppointmentLinkageModel[];
        veterinarianId: string;
        patientIds: string[];
        rescheduleData?: RescheduleData;
        specificEmployeeRequested: boolean;
        patientInsurancesMap?: Map<string, string[]>;
    }): Observable<ExtendedAppointmentChangeResponseModel> {
        const isRescheduling = !!rescheduleData;
        const appointmentsToCreate: AppointmentToCreateModel[] =
            (!isRescheduling &&
                patientIds.map((patientId, index: number) => {
                    return {
                        slotId: slotGroup.slots[index].slotId,
                        patientId: patientId,
                        linkages: appointmentLinkageModels,
                        reason,
                        specificEmployeeRequested,
                        patientInsuranceIds: patientInsurancesMap?.has(patientId) ? patientInsurancesMap.get(patientId) : [],
                    };
                })) ||
            null;
        const appointmentsToReschedule: AppointmentToRescheduleModel[] =
            (isRescheduling && [
                {
                    slotId: slotGroup.slots[0].slotId,
                    patientId: rescheduleData.patient.patientId,
                    appointmentToDelete: rescheduleData.appointment.id,
                    reason,
                    specificEmployeeRequested,
                    patientInsuranceIds: patientInsurancesMap.get(rescheduleData.patient.patientId) ?? [],
                },
            ]) ||
            null;
        const appointmentChangeRequestModel: AppointmentChangeRequestModel = {
            veterinarianId,
            appointmentsToCreate,
            appointmentsToReschedule,
        };
        return this.executeChange(appointmentChangeRequestModel, appointmentLinkageModels);
    }

    private cancelAppointments(appointments: AppointmentViewModel[]): Observable<ExtendedAppointmentChangeResponseModel[]> {
        return zip(
            appointments.map((appointment) => {
                const appointmentChangeRequest: AppointmentChangeRequestModel = {
                    veterinarianId: appointment.veterinarian.id,
                    appointmentsToCancel: [{ appointmentToDelete: appointment.appointment.id }],
                };
                return this.executeChange(appointmentChangeRequest, appointment.linkageModels);
            }),
        );
    }

    private executeChange(
        appointmentChangeRequestModel: AppointmentChangeRequestModel,
        appointmentLinkageModels: AppointmentLinkageModel[],
    ): Observable<ExtendedAppointmentChangeResponseModel> {
        return this.appointmentBackendService
            .change(appointmentChangeRequestModel, this.config.API_VERSION, this.loginService.getToken())
            .pipe(
                switchMap((response) => {
                    const appointmentIds = [
                        ...response.created,
                        ...response.deleted,
                        ...Object.keys(response.rescheduled),
                        ...Object.values(response.rescheduled),
                    ];
                    return this.getAppointments({ filterAppointmentIds: appointmentIds }).pipe(
                        map((appointments) => {
                            return {
                                created: response.created,
                                deleted: response.deleted,
                                rescheduled: response.rescheduled,
                                appointmentMap: new Map(appointments.map((appointment) => [appointment.id, appointment])),
                            };
                        }),
                    );
                }),
                tap((response) => {
                    const { created: createdAppointmentIds, deleted: cancelledAppointmentIds, rescheduled, appointmentMap } = response;
                    this.pxlApplicationStateService.raiseEventSpecific(
                        new AppointmentChangedEvent({
                            cancelledAppointmentIds,
                            createdAppointmentIds,
                            rescheduledAppointmentIds: rescheduled,
                            appointmentLinkageModels,
                            appointmentMap,
                            rescheduled: Object.values(response.rescheduled)
                                .map((newId) => appointmentMap.get(newId))
                                .filter(carriesValue),
                            created: createdAppointmentIds.map((id) => appointmentMap.get(id)).filter(carriesValue),
                            canceled: cancelledAppointmentIds.map((id) => appointmentMap.get(id)).filter(carriesValue),
                        }),
                    );
                }),
                take(1),
            );
    }
}
